export const INVOICE_STATUSES = [
  'draft', 'sent', 'overdue', 'reminder', 'demand_1', 'demand_2', 'collection', 'paid', 'cancelled',
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const OPEN_INVOICE_STATUSES: InvoiceStatus[] = ['sent', 'overdue', 'reminder', 'demand_1', 'demand_2', 'collection'];
export const DUNNING_STATUSES: InvoiceStatus[] = ['reminder', 'demand_1', 'demand_2', 'collection'];

export const VAT_TREATMENTS = ['nl_21', 'nl_9', 'nl_0', 'reverse_charge', 'outside_eu', 'exempt'] as const;
export type VatTreatment = (typeof VAT_TREATMENTS)[number];

export const VAT_RATE_BY_TREATMENT: Record<VatTreatment, number> = {
  nl_21: 21, nl_9: 9, nl_0: 0, reverse_charge: 0, outside_eu: 0, exempt: 0,
};

/** e-Boekhouden VAT code for each treatment (sales side). */
export const EB_VAT_CODE_BY_TREATMENT: Record<VatTreatment, string> = {
  nl_21: 'HOOG_VERK_21', nl_9: 'LAAG_VERK_9', nl_0: 'GEEN',
  reverse_charge: 'BI_EU_VERK', outside_eu: 'BU_EU_VERK', exempt: 'GEEN',
};

/** e-Boekhouden VAT code for each treatment (purchase side — the "INK" counterpart of the sales "VERK" codes above). */
export const EB_VAT_CODE_BY_TREATMENT_PURCHASE: Record<VatTreatment, string> = {
  nl_21: 'HOOG_INK_21', nl_9: 'LAAG_INK_9', nl_0: 'GEEN',
  reverse_charge: 'BI_EU_ACQ', outside_eu: 'BU_EU_ACQ', exempt: 'GEEN',
};

/**
 * Received (purchase) invoices in the digital dossier: `pending` needs a look
 * before it can be booked (a fresh upload, or a match that changed), `booked`
 * is already a mutation in e-Boekhouden, `duplicate` matched something
 * already on file — locally or in e-Boekhouden — and is held back from
 * booking until someone clears it, `ignored` is kept in the archive but
 * deliberately never booked (e.g. a credit note handled elsewhere).
 */
export const PURCHASE_INVOICE_STATUSES = ['pending', 'booked', 'duplicate', 'ignored'] as const;
export type PurchaseInvoiceStatus = (typeof PURCHASE_INVOICE_STATUSES)[number];

export const CADENCES = ['monthly', 'quarterly', 'yearly'] as const;
export type Cadence = (typeof CADENCES)[number];

export const SCHEDULE_STATUSES = ['active', 'paused', 'ended'] as const;
export type ScheduleStatus = (typeof SCHEDULE_STATUSES)[number];

export const TICKET_PRIORITIES = ['low', 'normal', 'high'] as const;
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];

export const TICKET_STATUSES = ['new', 'in_progress', 'waiting_on_customer', 'resolved', 'closed'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const MAIL_KINDS = ['invoice', 'reminder', 'demand_1', 'demand_2', 'ticket_reply', 'portal_invite', 'other'] as const;
export type MailKind = (typeof MAIL_KINDS)[number];

export const MAIL_STATUSES = ['queued', 'sent', 'delivered', 'opened', 'failed', 'bounced'] as const;
export type MailStatus = (typeof MAIL_STATUSES)[number];

export const TEMPLATE_KINDS = ['invoice', 'reminder', 'demand', 'ticket'] as const;
export type TemplateKind = (typeof TEMPLATE_KINDS)[number];

export const TEMPLATE_STATUSES = ['live', 'draft'] as const;
export type TemplateStatus = (typeof TEMPLATE_STATUSES)[number];

/** `crm` works in the CRM only: no invoices, customers, amounts or settings. */
export const ROLES = ['admin', 'staff', 'crm', 'customer'] as const;
export type Role = (typeof ROLES)[number];

export const CUSTOMER_TYPES = ['company', 'individual'] as const;
export type CustomerType = (typeof CUSTOMER_TYPES)[number];

export const PAYMENT_METHODS = ['bank_transfer', 'ideal', 'direct_debit'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const DUNNING_ACTIONS = ['mail', 'mail_registered', 'task'] as const;
export type DunningAction = (typeof DUNNING_ACTIONS)[number];

/** Merge fields available in every PDF layout and mail body. */
export const TEMPLATE_MERGE_FIELDS = [
  'customer.name', 'customer.contact', 'customer.first_name', 'customer.last_name', 'invoice.number', 'invoice.subject',
  'invoice.total', 'invoice.outstanding', 'invoice.due_date', 'invoice.issued_date', 'invoice.payment_days', 'invoice.reference',
  'dunning.fee', 'company.name', 'company.contact', 'company.first_name', 'company.last_name', 'company.iban', 'company.account_holder', 'company.email', 'company.phone', 'portal.link',
] as const;

/**
 * Rompslomp-style single-brace fields, accepted in mail templates next to the
 * {{group.field}} form so templates can be pasted over from Rompslomp as-is.
 * `{if veld}…{endif}` keeps the block only when the field is non-empty, and
 * link fields accept `{veld title=Klik hier}`.
 */
export const ROMPSLOMP_MERGE_FIELDS: Record<string, string> = {
  aan_voornaam: 'customer.first_name', aan_achternaam: 'customer.last_name', aan_bedrijfsnaam: 'customer.name',
  factuur_nummer: 'invoice.number', factuur_beschrijving: 'invoice.subject', factuur_datum: 'invoice.issued_date',
  verval_datum: 'invoice.due_date', verval_dagen: 'invoice.payment_days', factuur_bedrag: 'invoice.total',
  factuur_onbetaald_bedrag: 'invoice.outstanding', factuur_betaallink: 'payment.link', factuur_betaalknop: 'payment.link',
  onderteken_link: 'sign.link', van_voornaam: 'company.first_name', van_achternaam: 'company.last_name',
  van_bedrijfsnaam: 'company.name', bedrijf_rekeningnummer: 'company.iban', portaal_link: 'portal.link',
};

// ---- CRM (AVI-13) ------------------------------------------------------------

/**
 * The built-in segments every company has. A company adds its own next to
 * these (crm_segments), so an account's segment is any of these keys or one of
 * the company's own.
 */
export const CRM_SEGMENTS = ['ncc', 'airline', 'other'] as const;
export type BuiltinCrmSegment = (typeof CRM_SEGMENTS)[number];
/** A segment key: built-in or added by the company. */
export type CrmSegment = string;
export const CRM_BUILTIN_SEGMENT_NAMES: Record<BuiltinCrmSegment, string> = { ncc: 'NCC', airline: 'Airline', other: 'Other' };

export const CRM_OPERATOR_TYPES = ['aoc_airline', 'aoc_business', 'aoc_helicopter', 'ncc', 'other'] as const;
export type CrmOperatorType = (typeof CRM_OPERATOR_TYPES)[number];

export const CRM_ACCOUNT_SOURCES = ['icp_import', 'website', 'referral', 'event', 'manual'] as const;
export type CrmAccountSource = (typeof CRM_ACCOUNT_SOURCES)[number];

/**
 * Where an organisation stands with us. The first five are ordered and only
 * ever advance on their own (see services/crmRules.ts); `lost` and
 * `do_not_contact` sit outside that order and are set by a closed deal or by
 * hand.
 */
export const CRM_LIFECYCLES = ['target', 'contacted', 'engaged', 'opportunity', 'customer', 'lost', 'do_not_contact'] as const;
export type CrmLifecycle = (typeof CRM_LIFECYCLES)[number];

export const CRM_PERSONAS = ['safety_manager', 'compliance_monitoring_manager', 'accountable_manager', 'quality_manager', 'other'] as const;
export type CrmPersona = (typeof CRM_PERSONAS)[number];

export const CRM_LAWFUL_BASES = ['legitimate_interest', 'consent', 'contract'] as const;
export type CrmLawfulBasis = (typeof CRM_LAWFUL_BASES)[number];

/**
 * The pipeline, in funnel order. `lost` closes a deal from any open stage;
 * `paying` is reached only when the first invoice is paid, never by hand.
 */
export const DEAL_STAGES = ['qualified', 'conversation', 'demo_scheduled', 'demo_held', 'proposal_sent', 'won', 'paying', 'lost'] as const;
export type DealStage = (typeof DEAL_STAGES)[number];

export const OPEN_DEAL_STAGES: DealStage[] = ['qualified', 'conversation', 'demo_scheduled', 'demo_held', 'proposal_sent'];

export const DEAL_LOST_REASONS = ['no_budget', 'no_need', 'competitor', 'timing', 'no_response', 'other'] as const;
export type DealLostReason = (typeof DEAL_LOST_REASONS)[number];

export const CRM_ACTIVITY_KINDS = ['note', 'call', 'email', 'linkedin', 'meeting', 'demo', 'task'] as const;
export type CrmActivityKind = (typeof CRM_ACTIVITY_KINDS)[number];

/** Kinds that are contact with a person and so carry a direction; outbound ones are what the opt-out and the weekly cap guard. */
export const CRM_CONTACT_KINDS: CrmActivityKind[] = ['call', 'email', 'linkedin'];

export const CRM_ACTIVITY_DIRECTIONS = ['inbound', 'outbound'] as const;
export type CrmActivityDirection = (typeof CRM_ACTIVITY_DIRECTIONS)[number];

/** `system`: written by AviaConsole itself, e.g. a deal moving to paying when its invoice is paid. */
export const CRM_ACTIVITY_SOURCES = ['web', 'mcp', 'import', 'website', 'system'] as const;
export type CrmActivitySource = (typeof CRM_ACTIVITY_SOURCES)[number];
